/**
 * Outreach Warm Up page – sender accounts send warm-up templates to
 * receiver accounts, which open, spam-rescue, and reply automatically.
 * Includes management of warm-up email templates and reply templates.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card, Button, Select, InputNumber, Space, Table, Tag, Progress,
  message, Typography, Divider, Alert, Descriptions, Tooltip, Empty,
  Modal, Form, Input, Switch, Popconfirm, Tabs,
} from 'antd';
import {
  FireOutlined, StopOutlined, ReloadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  MailOutlined, InboxOutlined, WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
  SenderAccount, WarmupJob, WarmupEvent, StartWarmupRequest,
  WarmupEmailTemplate, WarmupReplyTemplate,
} from '../types';
import { outreachSenderAccountsApi, outreachWarmupApi } from '../api';

const { Text, Title, Paragraph } = Typography;

const ACTION_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  send:         { label: 'Send',        color: 'blue',   icon: <MailOutlined /> },
  open:         { label: 'Open',        color: 'cyan',   icon: <InboxOutlined /> },
  spam_rescue:  { label: 'Spam Rescue', color: 'orange', icon: <WarningOutlined /> },
  reply:        { label: 'Reply',       color: 'green',  icon: <MailOutlined /> },
  file_warmup:  { label: 'File Warmup', color: 'purple', icon: <InboxOutlined /> },
};

const STATUS_COLORS: Record<string, string> = {
  running:   'processing',
  completed: 'success',
  failed:    'error',
  cancelled: 'default',
};

/** Sentinel value for "pick any available template at random". */
const RANDOM_OPTION = '__random__';

/** Keep Random exclusive: selecting it clears others; selecting a specific clears Random. */
function withExclusiveRandom(prev: string[], next: string[]): string[] {
  const justPickedRandom = next.includes(RANDOM_OPTION) && !prev.includes(RANDOM_OPTION);
  if (justPickedRandom) return [RANDOM_OPTION];
  if (next.includes(RANDOM_OPTION) && next.length > 1) {
    return next.filter(v => v !== RANDOM_OPTION);
  }
  return next;
}

export const OutreachWarmUp: React.FC = () => {
  // data
  const [accounts, setAccounts] = useState<SenderAccount[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<WarmupEmailTemplate[]>([]);
  const [replyTemplates, setReplyTemplates] = useState<WarmupReplyTemplate[]>([]);
  const [jobs, setJobs] = useState<WarmupJob[]>([]);
  const [activeJob, setActiveJob] = useState<WarmupJob | null>(null);

  // form
  const [selectedSenders, setSelectedSenders] = useState<string[]>([]);
  const [selectedReceivers, setSelectedReceivers] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [selectedReplyTemplates, setSelectedReplyTemplates] = useState<string[]>([]);
  const [rounds, setRounds] = useState(1);
  const [replyCount, setReplyCount] = useState(1);
  const [minDelay, setMinDelay] = useState(30);
  const [maxDelay, setMaxDelay] = useState(120);

  // state
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // template modals
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<WarmupEmailTemplate | null>(null);
  const [emailForm] = Form.useForm();
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [editingReply, setEditingReply] = useState<WarmupReplyTemplate | null>(null);
  const [replyForm] = Form.useForm();

  // ---------------------------------------------------------------
  // load data
  // ---------------------------------------------------------------
  const loadAccounts = useCallback(async () => {
    try {
      const res = await outreachSenderAccountsApi.list();
      setAccounts(res.items);
    } catch { message.error('Failed to load accounts'); }
  }, []);

  const loadEmailTemplates = useCallback(async () => {
    try {
      const res = await outreachWarmupApi.listTemplates();
      setEmailTemplates(res.items);
    } catch { /* silent */ }
  }, []);

  const loadReplyTemplates = useCallback(async () => {
    try {
      const res = await outreachWarmupApi.listReplyTemplates();
      setReplyTemplates(res.items);
    } catch { /* silent */ }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const res = await outreachWarmupApi.listJobs();
      setJobs(res.jobs);
      const running = res.jobs.find(j => j.status === 'running');
      if (running) setActiveJob(running);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadAccounts();
    loadEmailTemplates();
    loadReplyTemplates();
    loadJobs();
  }, [loadAccounts, loadEmailTemplates, loadReplyTemplates, loadJobs]);

  // ---------------------------------------------------------------
  // polling for active job
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!activeJob || activeJob.status !== 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    const jobId = activeJob.job_id;
    const poll = async () => {
      try {
        const updated = await outreachWarmupApi.status(jobId);
        setActiveJob(updated);
        if (updated.status !== 'running') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          loadJobs();
          if (updated.status === 'completed') message.success('Warm-up completed!');
          else if (updated.status === 'failed') message.error(`Warm-up failed: ${updated.error}`);
        }
      } catch { /* keep polling */ }
    };
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.job_id, activeJob?.status, loadJobs]);

  // ---------------------------------------------------------------
  // start / cancel warm-up
  // ---------------------------------------------------------------
  const handleStart = async () => {
    if (selectedSenders.length < 1) {
      message.warning('Select at least 1 sender account');
      return;
    }
    if (selectedReceivers.length < 1) {
      message.warning('Select at least 1 receiver account');
      return;
    }
    // At least one distinct sender→receiver pair (same account can't email itself)
    const hasDistinctPair = selectedSenders.some(s => selectedReceivers.some(r => r !== s));
    if (!hasDistinctPair) {
      message.warning('Sender and receiver must be different accounts');
      return;
    }
    if (selectedTemplates.length < 1) {
      message.warning('Select at least 1 warm-up template, or Random');
      return;
    }
    if (selectedReplyTemplates.length < 1) {
      message.warning('Select at least 1 reply template, or Random');
      return;
    }
    if (minDelay > maxDelay) {
      message.warning('Min delay must be ≤ max delay');
      return;
    }
    const useRandomWarmup = selectedTemplates.includes(RANDOM_OPTION);
    const useRandomReply = selectedReplyTemplates.includes(RANDOM_OPTION);
    setStarting(true);
    try {
      const req: StartWarmupRequest = {
        sender_account_ids: selectedSenders,
        receiver_account_ids: selectedReceivers,
        campaign_ids: [],
        warmup_template_ids: useRandomWarmup ? [] : selectedTemplates,
        use_random_warmup_templates: useRandomWarmup,
        reply_template_ids: useRandomReply ? [] : selectedReplyTemplates,
        use_random_reply_templates: useRandomReply,
        rounds,
        reply_count: replyCount,
        min_delay_seconds: minDelay,
        max_delay_seconds: maxDelay,
      };
      const job = await outreachWarmupApi.start(req);
      setActiveJob(job);
      message.success('Warm-up started!');
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to start warm-up');
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async () => {
    if (!activeJob) return;
    try {
      await outreachWarmupApi.cancel(activeJob.job_id);
      message.info('Warm-up cancelled');
      setActiveJob(prev => prev ? { ...prev, status: 'cancelled' } : null);
      loadJobs();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleRefreshJobs = async () => {
    setLoading(true);
    await loadJobs();
    setLoading(false);
  };

  // ---------------------------------------------------------------
  // email template CRUD
  // ---------------------------------------------------------------
  const openCreateEmail = () => {
    setEditingEmail(null);
    emailForm.resetFields();
    emailForm.setFieldsValue({ is_active: true });
    setEmailModalOpen(true);
  };

  const openEditEmail = (tpl: WarmupEmailTemplate) => {
    setEditingEmail(tpl);
    emailForm.setFieldsValue({
      subject: tpl.subject, body: tpl.body, is_active: tpl.is_active,
    });
    setEmailModalOpen(true);
  };

  const submitEmail = async (values: any) => {
    try {
      if (editingEmail) {
        await outreachWarmupApi.updateTemplate(editingEmail.id, values);
        message.success('Template updated');
      } else {
        await outreachWarmupApi.createTemplate(values);
        message.success('Template created');
      }
      setEmailModalOpen(false);
      loadEmailTemplates();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to save template');
    }
  };

  const deleteEmail = async (tpl: WarmupEmailTemplate) => {
    try {
      await outreachWarmupApi.deleteTemplate(tpl.id);
      message.success('Template deleted');
      setSelectedTemplates(prev => prev.filter(id => id !== tpl.id));
      loadEmailTemplates();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---------------------------------------------------------------
  // reply template CRUD
  // ---------------------------------------------------------------
  const openCreateReply = () => {
    setEditingReply(null);
    replyForm.resetFields();
    replyForm.setFieldsValue({ is_active: true });
    setReplyModalOpen(true);
  };

  const openEditReply = (tpl: WarmupReplyTemplate) => {
    setEditingReply(tpl);
    replyForm.setFieldsValue({ body: tpl.body, is_active: tpl.is_active });
    setReplyModalOpen(true);
  };

  const submitReply = async (values: any) => {
    try {
      if (editingReply) {
        await outreachWarmupApi.updateReplyTemplate(editingReply.id, values);
        message.success('Reply template updated');
      } else {
        await outreachWarmupApi.createReplyTemplate(values);
        message.success('Reply template created');
      }
      setReplyModalOpen(false);
      loadReplyTemplates();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to save reply template');
    }
  };

  const deleteReply = async (tpl: WarmupReplyTemplate) => {
    try {
      await outreachWarmupApi.deleteReplyTemplate(tpl.id);
      message.success('Reply template deleted');
      setSelectedReplyTemplates(prev => prev.filter(id => id !== tpl.id));
      loadReplyTemplates();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---------------------------------------------------------------
  // columns
  // ---------------------------------------------------------------
  const eventColumns: ColumnsType<WarmupEvent> = [
    {
      title: 'Action', dataIndex: 'action', key: 'action', width: 130,
      render: (a: string) => {
        const meta = ACTION_LABELS[a] || { label: a, color: 'default', icon: null };
        return <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>;
      },
    },
    { title: 'From', dataIndex: 'from_email', key: 'from', ellipsis: true },
    { title: 'To', dataIndex: 'to_email', key: 'to', ellipsis: true },
    {
      title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (s: string) => (
        <Tag color={s === 'success' ? 'green' : 'red'}
          icon={s === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {s}
        </Tag>
      ),
    },
    {
      title: 'Error', dataIndex: 'error', key: 'error', ellipsis: true, width: 200,
      render: (v: string | null) => v ? <Tooltip title={v}><Text type="danger" ellipsis>{v}</Text></Tooltip> : '—',
    },
    {
      title: 'Time', dataIndex: 'timestamp', key: 'timestamp', width: 170,
      render: (v: string | null) => {
        if (!v) return '—';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '—' : d.toLocaleString();
      },
    },
  ];

  const jobColumns: ColumnsType<WarmupJob> = [
    { title: 'Job ID', dataIndex: 'job_id', key: 'job_id', width: 120, ellipsis: true,
      render: (id: string) => <Text copyable={{ text: id }} style={{ fontSize: 12 }}>{id.substring(0, 8)}…</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (s: string) => {
        const color = STATUS_COLORS[s] || 'default';
        const icon = s === 'running' ? <SyncOutlined spin /> :
                     s === 'completed' ? <CheckCircleOutlined /> :
                     s === 'failed' ? <CloseCircleOutlined /> : undefined;
        return <Tag color={color} icon={icon}>{s.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Progress', key: 'progress', width: 200,
      render: (_, r) => {
        const pct = r.total_pairs > 0 ? Math.round((r.completed_pairs / r.total_pairs) * 100) : 0;
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Progress percent={pct} size="small" status={r.status === 'failed' ? 'exception' : undefined} />
            <Text type="secondary" style={{ fontSize: 11 }}>{r.completed_pairs}/{r.total_pairs} pairs</Text>
          </Space>
        );
      },
    },
    {
      title: 'Events', key: 'event_counts', width: 260,
      render: (_, r) => {
        const counts: Record<string, { success: number; failed: number }> = {};
        for (const ev of r.events) {
          if (!counts[ev.action]) counts[ev.action] = { success: 0, failed: 0 };
          if (ev.status === 'success') counts[ev.action].success++;
          else counts[ev.action].failed++;
        }
        return (
          <Space size={4} wrap>
            {Object.entries(counts).map(([action, c]) => {
              const meta = ACTION_LABELS[action] || { label: action, color: 'default' };
              return (
                <Tooltip key={action} title={`${c.success} ok, ${c.failed} failed`}>
                  <Tag color={meta.color}>{meta.label}: {c.success}{c.failed > 0 ? ` / ${c.failed} err` : ''}</Tag>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Started', dataIndex: 'started_at', key: 'started_at', width: 170,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Error', dataIndex: 'error', key: 'error', ellipsis: true,
      render: (v: string | null) => v ? <Text type="danger" ellipsis>{v}</Text> : '—',
    },
  ];

  const emailTemplateColumns: ColumnsType<WarmupEmailTemplate> = [
    { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
    {
      title: 'Body', dataIndex: 'body', key: 'body', ellipsis: true,
      render: (v: string) => v?.length > 100 ? `${v.slice(0, 100)}…` : v,
    },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 90,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_, r) => (
        <Space size="small" style={{ whiteSpace: 'nowrap', paddingRight: 8 }}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditEmail(r)}>Edit</Button>
          <Popconfirm title="Delete this template?" onConfirm={() => deleteEmail(r)} okText="Delete" okType="danger">
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const replyTemplateColumns: ColumnsType<WarmupReplyTemplate> = [
    { title: 'Reply Text', dataIndex: 'body', key: 'body', ellipsis: true },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 90,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_, r) => (
        <Space size="small" style={{ whiteSpace: 'nowrap', paddingRight: 8 }}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditReply(r)}>Edit</Button>
          <Popconfirm title="Delete this reply template?" onConfirm={() => deleteReply(r)} okText="Delete" okType="danger">
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const isRunning = activeJob?.status === 'running';

  const spamRescueCount = activeJob?.events.filter(e => e.action === 'spam_rescue' && e.status === 'success').length ?? 0;
  const totalSends = activeJob?.events.filter(e => e.action === 'send').length ?? 0;
  const successSends = activeJob?.events.filter(e => e.action === 'send' && e.status === 'success').length ?? 0;
  const totalReplies = activeJob?.events.filter(e => e.action === 'reply').length ?? 0;
  const successReplies = activeJob?.events.filter(e => e.action === 'reply' && e.status === 'success').length ?? 0;

  // ---------------------------------------------------------------
  // render: the "Run" tab content
  // ---------------------------------------------------------------
  const runTab = (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Warm-up: sender accounts send your warm-up email templates to receiver accounts, which open them, move spam to inbox, and reply automatically. All warm-up sends and replies are filed under a Gmail label/folder named Warmup so inboxes stay clear."
      />

      <Card
        title="Start Warm-Up Cycle"
        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Sender Accounts</Text>
            <Select
              mode="multiple"
              placeholder="Accounts that will send warm-up email templates"
              style={{ width: '100%' }}
              value={selectedSenders}
              onChange={setSelectedSenders}
              optionFilterProp="label"
              disabled={isRunning}
              options={accounts.map(a => ({
                value: a.id,
                label: `${a.display_name || a.account_key} — ${a.email}`,
              }))}
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Receiver Accounts</Text>
            <Select
              mode="multiple"
              placeholder="Accounts that will receive and reply to warm-up emails"
              style={{ width: '100%' }}
              value={selectedReceivers}
              onChange={setSelectedReceivers}
              optionFilterProp="label"
              disabled={isRunning}
              options={accounts.map(a => ({
                value: a.id,
                label: `${a.display_name || a.account_key} — ${a.email}`,
              }))}
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Warm-Up Email Templates</Text>
            <Select
              mode="multiple"
              placeholder="Select templates, or Random for any active template"
              style={{ width: '100%' }}
              value={selectedTemplates}
              onChange={(vals) => setSelectedTemplates(prev => withExclusiveRandom(prev, vals))}
              optionFilterProp="label"
              disabled={isRunning}
              options={[
                { value: RANDOM_OPTION, label: 'Random (any active template)' },
                ...emailTemplates.filter(t => t.is_active).map(t => ({
                  value: t.id,
                  label: t.subject,
                })),
              ]}
              notFoundContent="No warm-up templates yet — add one in the Templates tab"
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Reply Templates</Text>
            <Select
              mode="multiple"
              placeholder="Select reply templates, or Random for any active reply"
              style={{ width: '100%' }}
              value={selectedReplyTemplates}
              onChange={(vals) => setSelectedReplyTemplates(prev => withExclusiveRandom(prev, vals))}
              optionFilterProp="label"
              disabled={isRunning}
              options={[
                { value: RANDOM_OPTION, label: 'Random (any active reply)' },
                ...replyTemplates.filter(t => t.is_active).map(t => ({
                  value: t.id,
                  label: t.body.length > 80 ? `${t.body.slice(0, 80)}…` : t.body,
                })),
              ]}
              notFoundContent="No reply templates yet — add one in the Templates tab (Random still uses built-in defaults)"
            />
          </div>

          <Space size="large" wrap>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Rounds</Text>
              <Tooltip title="Number of full send-then-reply cycles per sender→receiver pair">
                <InputNumber min={1} max={50} value={rounds} onChange={v => setRounds(v ?? 1)} disabled={isRunning} />
              </Tooltip>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Replies per email</Text>
              <Tooltip title="How many randomized replies to send per warm-up email">
                <InputNumber min={1} max={10} value={replyCount} onChange={v => setReplyCount(v ?? 1)} disabled={isRunning} />
              </Tooltip>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Min delay (s)</Text>
              <Tooltip title="Minimum random delay in seconds before each reply">
                <InputNumber min={5} max={600} value={minDelay} onChange={v => setMinDelay(v ?? 30)} disabled={isRunning} />
              </Tooltip>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Max delay (s)</Text>
              <Tooltip title="Maximum random delay in seconds before each reply">
                <InputNumber min={10} max={600} value={maxDelay} onChange={v => setMaxDelay(v ?? 120)} disabled={isRunning} />
              </Tooltip>
            </div>
          </Space>

          <Divider style={{ margin: '8px 0' }} />

          <Space>
            {!isRunning ? (
              <Button
                type="primary"
                icon={<FireOutlined />}
                onClick={handleStart}
                loading={starting}
                disabled={
                  selectedSenders.length < 1
                  || selectedReceivers.length < 1
                  || selectedTemplates.length < 1
                  || selectedReplyTemplates.length < 1
                  || !selectedSenders.some(s => selectedReceivers.some(r => r !== s))
                }
              >
                Start Warm-Up
              </Button>
            ) : (
              <Button danger icon={<StopOutlined />} onClick={handleCancel}>
                Cancel Warm-Up
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {activeJob && (
        <Card
          title={
            <Space>
              <span>Active Job</span>
              <Tag color={STATUS_COLORS[activeJob.status] || 'default'}
                icon={activeJob.status === 'running' ? <SyncOutlined spin /> : undefined}>
                {activeJob.status.toUpperCase()}
              </Tag>
            </Space>
          }
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Progress">
              <Progress
                percent={activeJob.total_pairs > 0 ? Math.round((activeJob.completed_pairs / activeJob.total_pairs) * 100) : 0}
                size="small"
                style={{ width: 150 }}
                status={activeJob.status === 'failed' ? 'exception' : undefined}
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {activeJob.completed_pairs}/{activeJob.total_pairs} pairs
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Sends">{successSends}/{totalSends}</Descriptions.Item>
            <Descriptions.Item label="Replies">{successReplies}/{totalReplies}</Descriptions.Item>
            <Descriptions.Item label="Spam Rescued">
              {spamRescueCount > 0 ? <Tag color="orange">{spamRescueCount}</Tag> : <Tag color="green">0</Tag>}
            </Descriptions.Item>
          </Descriptions>

          {activeJob.current_action && (
            <Alert type="info" message={activeJob.current_action} style={{ marginBottom: 12 }} />
          )}
          {activeJob.error && (
            <Alert type="error" message={activeJob.error} style={{ marginBottom: 12 }} />
          )}

          <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>Event Log</Title>
          <Table
            columns={eventColumns}
            dataSource={activeJob.events}
            rowKey={(_, idx) => String(idx)}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true, size: 'small' }}
            scroll={{ x: 900 }}
          />
        </Card>
      )}

      <Card
        title={
          <Space>
            <span>Warm-Up History</span>
            <Button size="small" icon={<ReloadOutlined />} onClick={handleRefreshJobs} loading={loading}>Refresh</Button>
          </Space>
        }
        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        {jobs.length === 0 ? (
          <Empty description="No warm-up jobs yet. Start one above to test deliverability." />
        ) : (
          <Table
            columns={jobColumns}
            dataSource={jobs}
            rowKey="job_id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true, size: 'small' }}
            scroll={{ x: 1000 }}
          />
        )}
      </Card>
    </div>
  );

  // ---------------------------------------------------------------
  // render: the "Templates" tab content
  // ---------------------------------------------------------------
  const templatesTab = (
    <div>
      <Card
        title={
          <Space>
            <MailOutlined />
            <span>Warm-Up Email Templates</span>
          </Space>
        }
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateEmail}>Add Template</Button>}
        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          These templates provide the email content used during warm-up. One is picked at random for each send.
        </Paragraph>
        {emailTemplates.length === 0 ? (
          <Empty description="No warm-up email templates yet." />
        ) : (
          <Table
            columns={emailTemplateColumns}
            dataSource={emailTemplates}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
            style={{ marginRight: 8 }}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <MailOutlined />
            <span>Reply Templates</span>
          </Space>
        }
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateReply}>Add Reply</Button>}
        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Randomized reply bodies sent back to the original sender. If none are active, a built-in default set is used.
        </Paragraph>
        {replyTemplates.length === 0 ? (
          <Empty description="No reply templates yet — built-in defaults will be used." />
        ) : (
          <Table
            columns={replyTemplateColumns}
            dataSource={replyTemplates}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
            style={{ marginRight: 8 }}
          />
        )}
      </Card>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>Warm Up</span>
      </div>

      <Tabs
        defaultActiveKey="run"
        items={[
          { key: 'run', label: <span><FireOutlined /> Run</span>, children: runTab },
          { key: 'templates', label: <span><MailOutlined /> Templates</span>, children: templatesTab },
        ]}
      />

      {/* Email template modal */}
      <Modal
        title={editingEmail ? 'Edit Warm-Up Template' : 'Add Warm-Up Template'}
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={emailForm} layout="vertical" onFinish={submitEmail}>
          <Form.Item name="subject" label="Email Subject" rules={[{ required: true, message: 'Enter a subject' }]}>
            <Input placeholder="e.g. Following up on our chat" />
          </Form.Item>
          <Form.Item name="body" label="Email Body (HTML allowed)" rules={[{ required: true, message: 'Enter the body' }]}>
            <Input.TextArea rows={6} placeholder="Write the warm-up email content..." />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">{editingEmail ? 'Update' : 'Create'}</Button>
              <Button onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reply template modal */}
      <Modal
        title={editingReply ? 'Edit Reply Template' : 'Add Reply Template'}
        open={replyModalOpen}
        onCancel={() => setReplyModalOpen(false)}
        footer={null}
        destroyOnClose
        width={560}
      >
        <Form form={replyForm} layout="vertical" onFinish={submitReply}>
          <Form.Item name="body" label="Reply Text" rules={[{ required: true, message: 'Enter the reply text' }]}>
            <Input.TextArea rows={4} placeholder="e.g. Thanks for reaching out, this is really helpful!" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">{editingReply ? 'Update' : 'Create'}</Button>
              <Button onClick={() => setReplyModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
